import React from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const withCreds = { withCredentials: true };

export const inputCls = 'w-full bg-white border border-ink/15 rounded-xl px-4 py-2.5 text-ink text-sm placeholder-ink/40 focus:outline-none focus:border-honey focus:ring-2 focus:ring-honey/20';
export const smallInputCls = 'bg-white border border-ink/15 rounded-lg px-3 py-1.5 text-ink text-sm placeholder-ink/40 focus:outline-none focus:border-honey focus:ring-2 focus:ring-honey/20';
export const selectCls = 'bg-white border border-ink/15 rounded-xl px-3 py-2 text-ink text-sm focus:outline-none focus:border-honey';
export const btnPrimary = 'flex items-center justify-center gap-2 px-4 py-2 bg-honey text-ink rounded-xl font-bold text-sm hover:bg-[#FFC21F] transition-colors disabled:opacity-50';
export const btnSecondary = 'flex items-center justify-center gap-2 px-4 py-2 bg-white border border-ink/15 rounded-xl text-ink/80 hover:text-ink hover:border-ink/30 text-sm font-medium transition-colors disabled:opacity-50';
export const btnDanger = 'flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm font-bold hover:bg-red-100 transition-colors disabled:opacity-50';

export const ORDER_STATUSES = ['pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
export const STATUS_COLORS = {
  pending:    'bg-yellow-500/10 text-amber-600 border-yellow-500/20',
  paid:       'bg-blue-500/10 text-blue-600 border-blue-500/20',
  processing: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
  shipped:    'bg-purple-500/10 text-purple-600 border-purple-500/20',
  delivered:  'bg-green-500/10 text-green-600 border-green-500/20',
  cancelled:  'bg-red-500/10 text-red-600 border-red-500/20',
  refunded:   'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

export const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
export const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  return typeof d === 'string' ? d : JSON.stringify(d);
};

export function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-bold px-2 py-1 rounded-full border capitalize whitespace-nowrap ${STATUS_COLORS[status] || 'bg-ink/[0.04] text-ink/55 border-ink/10'}`}>
      {status}
    </span>
  );
}

export function Field({ label, hint, children, className = '' }) {
  return (
    <div className={className}>
      <label className="text-ink/55 text-xs uppercase tracking-widest mb-1 block">{label}</label>
      {children}
      {hint && <p className="text-ink/45 text-xs mt-1">{hint}</p>}
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
        {subtitle && <p className="text-ink/55 text-sm">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, width = 'max-w-2xl' }) {
  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className={`bg-white border border-ink/10 rounded-2xl w-full ${width}`}>
        <div className="flex items-center justify-between p-5 border-b border-ink/10">
          <h2 className="text-ink font-bold text-lg">{title}</h2>
          <button onClick={onClose} className="text-ink/55 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function Spinner() {
  return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-honey border-t-transparent rounded-full animate-spin" /></div>;
}

// Download a CSV from an admin endpoint (cookies are required, so a plain link won't do)
export async function downloadCsv(path, params = {}) {
  const r = await axios.get(`${API}${path}`, { params, responseType: 'blob', ...withCreds });
  const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'export.csv';
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

// Upload an image straight to Cloudinary using a signature from our backend; returns the hosted URL
export async function uploadImage(file, subfolder = 'products') {
  const { data: sig } = await axios.post(`${API}/admin/uploads/signature`, { subfolder }, withCreds);
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('folder', sig.folder);
  form.append('signature', sig.signature);
  const r = await fetch(sig.upload_url, { method: 'POST', body: form });
  const json = await r.json();
  if (!r.ok) throw new Error(json?.error?.message || 'Upload failed');
  return json.secure_url;
}

// <input type="datetime-local"> works in local time without a zone; store ISO (UTC) on the server
export const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const fromLocalInput = (value) => (value ? new Date(value).toISOString() : null);

const SCHEDULE_COLORS = {
  live: 'bg-green-500/10 text-green-600 border-green-500/20',
  scheduled: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  ended: 'bg-ink/[0.04] text-ink/45 border-ink/10',
  expired: 'bg-ink/[0.04] text-ink/45 border-ink/10',
  'used up': 'bg-orange-500/10 text-orange-600 border-orange-500/20',
  off: 'bg-ink/[0.04] text-ink/45 border-ink/10',
};

export function ScheduleBadge({ status }) {
  return <span className={`text-xs font-bold px-2 py-1 rounded-full border capitalize whitespace-nowrap ${SCHEDULE_COLORS[status] || SCHEDULE_COLORS.off}`}>{status}</span>;
}

export const discountLabel = (type, value) => (type === 'percent' ? `${value}% off` : type === 'free_shipping' ? 'Free shipping' : `$${Number(value).toFixed(2)} off`);
